import { BundleDiscountSystem } from "src/types/bundle-discounts/BundleDiscountSystem";
import { BundleDiscountType } from "src/types/bundle-discounts/BundleDiscountType";
import { KoalaInsertLineItem } from "src/types/koala/KoalaCart";
import { DiscountType } from "src/types/discount/DiscountType";
import getBundleDiscountBySku from "./getBundleDiscountBySku";
import { cloneDeep, sortBy } from "lodash";
import { BundleConfigManager } from "./bundleConfigManager";

export interface BundleProcessingResult {
  lineItems: KoalaInsertLineItem[];
  appliedBundle?: BundleDiscountSystem;
  discountSummary?: {
    totalDiscount: number;
    itemsDiscounted: number;
    bundleType: BundleDiscountType;
  };
}

/**
 * Dynamic Bundle Processor
 * Handles all bundle types without hardcoded logic
 */
export class DynamicBundleProcessor {
  private configManager: BundleConfigManager;

  constructor(bundleSystems: BundleDiscountSystem[]) {
    this.configManager = new BundleConfigManager(bundleSystems);
  }

  /**
   * Main method to apply bundle discounts to cart items
   */
  processBundleDiscounts(lineItems: KoalaInsertLineItem[]): BundleProcessingResult {
    const clonedItems = cloneDeep(lineItems);
    const totalQuantity = this.getTotalQuantity(clonedItems);

    // Get the best qualifying bundle
    const bundle = this.configManager.getBestBundle(totalQuantity);

    if (!bundle) {
      return {
        lineItems: this.clearDiscounts(clonedItems),
      };
    }

    // Apply the appropriate bundle logic based on type
    const processedItems = this.applyBundleByType(clonedItems, bundle);

    return {
      lineItems: processedItems,
      appliedBundle: bundle,
      discountSummary: this.calculateDiscountSummary(processedItems, bundle),
    };
  }

  /**
   * Apply bundle discount based on bundle type
   */
  private applyBundleByType(lineItems: KoalaInsertLineItem[], bundle: BundleDiscountSystem): KoalaInsertLineItem[] {
    switch (bundle.type) {
      case BundleDiscountType.BuyXGetY:
        return this.applyBuyXGetY(lineItems, bundle);

      case BundleDiscountType.BuyXGetYPercent:
        return this.applyBuyXGetYPercent(lineItems, bundle);

      case BundleDiscountType.BuyGet:
        return this.applyBuyGetFree(lineItems, bundle);

      case BundleDiscountType.bundle6:
        return this.applyNthItemDiscount(lineItems, bundle);

      case BundleDiscountType.NthItemDiscount:
        return this.applyNthItemDiscount(lineItems, bundle);

      case BundleDiscountType.PercentageOff:
      case BundleDiscountType.bundle2:
      case BundleDiscountType.bundle3:
      case BundleDiscountType.bundle4:
      case BundleDiscountType.bundle5:
        return this.applyPercentageOff(lineItems, bundle);

      default:
        return this.applyPercentageOff(lineItems, bundle);
    }
  }

  /**
   * Apply "Buy X Get Y" bundle (e.g., Buy 4 Get 2 free)
   */
  private applyBuyXGetY(lineItems: KoalaInsertLineItem[], bundle: BundleDiscountSystem): KoalaInsertLineItem[] {
    const buyQuantity = bundle.buyQuantity || bundle.minimumItems;
    const getQuantity = bundle.getQuantity || 1;
    const totalQuantity = this.getTotalQuantity(lineItems);

    // Calculate how many sets of buy+get we can have
    const totalSets = Math.floor(totalQuantity / buyQuantity);
    const freeItemsCount = totalSets * getQuantity;

    if (freeItemsCount > 0) {
      return this.applyDiscountToCheapestItems(lineItems, bundle, freeItemsCount, 100);
    }

    return lineItems;
  }

  /**
   * Apply "Buy X Get Y% off" bundle (e.g., Buy 3 Get 50% off 1)
   */
  private applyBuyXGetYPercent(lineItems: KoalaInsertLineItem[], bundle: BundleDiscountSystem): KoalaInsertLineItem[] {
    const buyQuantity = bundle.buyQuantity || bundle.minimumItems;
    const getQuantity = bundle.getQuantity || 1;
    const totalQuantity = this.getTotalQuantity(lineItems);
    const discountAmount = Object.values(bundle.discountsPerItem)[0]?.amount || 50;

    // Calculate how many sets we can have
    const totalSets = Math.floor(totalQuantity / buyQuantity);
    const discountedItemsCount = totalSets * getQuantity;

    if (discountedItemsCount > 0) {
      return this.applyDiscountToCheapestItems(lineItems, bundle, discountedItemsCount, discountAmount);
    }

    return lineItems;
  }

  /**
   * Apply traditional "Buy Get Free" bundle (100% off cheapest items)
   */
  private applyBuyGetFree(lineItems: KoalaInsertLineItem[], bundle: BundleDiscountSystem): KoalaInsertLineItem[] {
    const totalQuantity = this.getTotalQuantity(lineItems);
    const freeItemsCount = Math.floor(totalQuantity / bundle.minimumItems);

    if (freeItemsCount > 0) {
      return this.applyDiscountToCheapestItems(lineItems, bundle, freeItemsCount, 100);
    }

    return lineItems;
  }

  /**
   * Apply discount to Nth item (e.g., 50% off 3rd item)
   */
  private applyNthItemDiscount(lineItems: KoalaInsertLineItem[], bundle: BundleDiscountSystem): KoalaInsertLineItem[] {
    const nthItem = bundle.discountOnNthItem || 3;
    const discountAmount = Object.values(bundle.discountsPerItem)[0]?.amount || 50;
    const totalQuantity = this.getTotalQuantity(lineItems);

    // Apply discount to every Nth item
    const discountedItemsCount = Math.floor(totalQuantity / nthItem);

    if (discountedItemsCount > 0) {
      return this.applyDiscountToCheapestItems(lineItems, bundle, discountedItemsCount, discountAmount);
    }

    return lineItems;
  }

  /**
   * Apply percentage off to all items
   */
  private applyPercentageOff(lineItems: KoalaInsertLineItem[], bundle: BundleDiscountSystem): KoalaInsertLineItem[] {
    return lineItems.map((lineItem) => {
      const bundleDiscount = getBundleDiscountBySku(lineItem.sku, bundle);
      lineItem.discount = {
        type: DiscountType.bundle,
        name: bundleDiscount.bundleType,
        value: bundleDiscount.amount,
        valueType: bundleDiscount.type,
      };
      return lineItem;
    });
  }

  /**
   * Apply discount to cheapest items (used for Buy X Get Y scenarios)
   */
  private applyDiscountToCheapestItems(
    lineItems: KoalaInsertLineItem[],
    bundle: BundleDiscountSystem,
    discountedCount: number,
    discountAmount: number
  ): KoalaInsertLineItem[] {
    // Flatten items based on quantity
    const flattenedItems = lineItems.flatMap((lineItem) => {
      return Array(lineItem.quantity).fill({ ...lineItem, quantity: 1 });
    });

    // Sort by price (cheapest first for discounts)
    const sortedItems = sortBy(flattenedItems, "price");

    // Apply discount to cheapest items
    const discountedItems = sortedItems.slice(0, discountedCount);
    discountedItems.forEach((item) => {
      const bundleDiscount = getBundleDiscountBySku(item.sku, bundle);
      item.discount = {
        type: DiscountType.bundle,
        name: bundleDiscount.bundleType,
        value: discountAmount,
        valueType: bundleDiscount.type,
      };
    });

    // Map discounts back to original line items
    return lineItems.map((lineItem) => {
      const applicableDiscounts = discountedItems.filter((item) => item.sku === lineItem.sku);

      if (applicableDiscounts.length > 0) {
        const bundleDiscount = getBundleDiscountBySku(lineItem.sku, bundle);
        lineItem.discount = {
          type: DiscountType.bundle,
          name: bundleDiscount.bundleType,
          value: (applicableDiscounts.length / lineItem.quantity) * discountAmount,
          valueType: bundleDiscount.type,
        };
      } else {
        delete lineItem.discount;
      }

      return lineItem;
    });
  }

  /**
   * Clear all discounts from line items
   */
  private clearDiscounts(lineItems: KoalaInsertLineItem[]): KoalaInsertLineItem[] {
    return lineItems.map((item) => {
      delete item.discount;
      return item;
    });
  }

  /**
   * Calculate total quantity of items
   */
  private getTotalQuantity(lineItems: KoalaInsertLineItem[]): number {
    return lineItems.reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Calculate discount summary
   */
  private calculateDiscountSummary(lineItems: KoalaInsertLineItem[], bundle: BundleDiscountSystem) {
    const itemsWithDiscounts = lineItems.filter((item) => item.discount);
    const totalDiscount = itemsWithDiscounts.reduce((total, item) => {
      if (item.discount && item.price) {
        const discountValue =
          item.discount.valueType === "percentage"
            ? (item.price * item.quantity * item.discount.value) / 100
            : item.discount.value;
        return total + discountValue;
      }
      return total;
    }, 0);

    return {
      totalDiscount,
      itemsDiscounted: itemsWithDiscounts.length,
      bundleType: bundle.type,
    };
  }

  /**
   * Get the config manager for external access
   */
  getConfigManager(): BundleConfigManager {
    return this.configManager;
  }
}
